import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Typography,
    Box,
    Paper,
    IconButton,
    Drawer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';

import TimeHelper from '../../../utils/time';

interface AkbankTransactionLogsDrawerProps {
    open: boolean;
    onClose: () => void;
    logs: any[];
}

export const AkbankTransactionLogsDrawer: FC<AkbankTransactionLogsDrawerProps> = ({
    open,
    onClose,
    logs,
}) => {
    const { t } = useTranslation();
    const isTablet = useMediaQuery('(max-width: 960px)', { noSsr: true });

    const formatJSON = (jsonString: string) => {
        try {
            const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
            return JSON.stringify(parsed, null, 2);
        } catch {
            return jsonString;
        }
    };

    return (
        <Drawer
            className="akbank-transaction-logs-drawer"
            open={open}
            anchor="right"
            onClose={(event: any, reason: any) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    onClose();
                }
            }}
            slotProps={{
                backdrop: {
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    },
                },
                paper: {
                    sx: {
                        width: isTablet ? '100%' : '80%',
                        backgroundColor: '#1e1e1e',
                    },
                },
            }}
        >
            <Paper
                className="akbank-transaction-logs-drawer-content"
                sx={{
                    height: '100%',
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff',
                    padding: 3,
                    overflow: 'auto',
                }}
            >
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}
                >
                    <Typography variant="h6" sx={{ color: '#ffffff' }}>
                        {t('akbankTransactionStatusList.requestResponseLogs')}
                    </Typography>
                    <IconButton
                        className="akbank-transaction-logs-drawer-close-button"
                        onClick={onClose}
                        sx={{ color: '#ffffff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                {logs.map((log: any, index: number) => (
                    <Paper
                        key={index}
                        className="akbank-transaction-logs-drawer-log-item"
                        sx={{
                            backgroundColor: '#2d2d2d',
                            padding: 2,
                            marginBottom: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: '#ffffff', marginBottom: 1 }}>
                            {t('akbankTransactionStatusList.log')} #{index + 1}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.id')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                    {log.id || '-'}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.requestDate')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                    {log.requestDate ? TimeHelper.formatDateHourSecond(log.requestDate) : '-'}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.responseDate')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                    {log.responseDate ? TimeHelper.formatDateHourSecond(log.responseDate) : '-'}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.responseCode')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                    {log.responseCode || '-'}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.endpoint')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                                    {log.endpoint || '-'}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.status')}:
                                </Typography>
                                <Typography variant="body1" sx={{ color: log.status ? '#4caf50' : '#f44336' }}>
                                    {log.status ? t('general.completed') : t('general.failed')}
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.requestData')}:
                                </Typography>
                                <Paper
                                    sx={{
                                        backgroundColor: '#000000',
                                        padding: 2,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        maxHeight: '300px',
                                    }}
                                >
                                    <pre
                                        style={{
                                            color: '#00ff00',
                                            margin: 0,
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                        }}
                                    >
                                        {formatJSON(log.requestData || '{}')}
                                    </pre>
                                </Paper>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <Typography variant="body2" sx={{ color: '#cccccc', marginBottom: 0.5 }}>
                                    {t('akbankTransactionStatusList.responseData')}:
                                </Typography>
                                <Paper
                                    sx={{
                                        backgroundColor: '#000000',
                                        padding: 2,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        maxHeight: '300px',
                                    }}
                                >
                                    <pre
                                        style={{
                                            color: '#00ff00',
                                            margin: 0,
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                        }}
                                    >
                                        {formatJSON(log.responseData || '{}')}
                                    </pre>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                {logs.length === 0 && (
                    <Typography variant="body1" sx={{ color: '#ffffff', textAlign: 'center', marginTop: 4 }}>
                        {t('akbankTransactionStatusList.noLogsFound')}
                    </Typography>
                )}
            </Paper>
        </Drawer>
    );
};

